import { useEffect, useId, useRef, useState } from "react";
import { Button } from "../components/ui/button";
import { ApiError } from "../lib/api";
import { cn } from "../lib/utils";
import type { SerreLivresReponse, SerreLivresRequete } from "./contract";
import { ALERTE, CARTE } from "./silhouettes";
import { declencherTelechargement, useLiensDeFichiers } from "./silhouettes-fichiers";
import {
  classerEchec,
  type Echec,
  envoiTropLourd,
  envoyerAssemblage,
  fichierDuJeu,
  IMPRIMANTES,
  type Imprimante,
  jeuDe,
  memeRequete,
  plateauxAvantAssemblage,
} from "./silhouettes-jeux";
import {
  LIBELLE_ASSEMBLAGE_EN_COURS,
  libelleDuTelechargement,
  notePourLImprimante,
  phraseDuRefus,
  SUITE_DU_REFUS,
  TEXTE_ASSEMBLAGE_CADUC,
  TITRE_DES_FICHIERS_PRETS,
  texteDEchec,
} from "./silhouettes-serre-livres-textes";
import type { Colonne } from "./silhouettes-verdict";

/**
 * Les deux jeux complets du mode serre-livres (#337, spec section 4) : un 3MF par imprimante, P1S et
 * A1 mini, assembles par `products` a partir des deux 3MF de figurines.
 *
 * Le premier clic envoie la requete (`POST /api/silhouettes/serre-livres`) et telecharge le fichier
 * demande des que la reponse arrive. **La reponse est gardee tant que rien ne change** : les deux
 * 3MF, les noms, les miroirs et les teintes (`memeRequete`). Un seul calcul fait donc les deux jeux,
 * et le second telechargement part tout de suite, de la reponse deja la. Des qu'un de ces elements
 * change, la reponse n'est plus celle de l'ecran : elle est ecartee, ses liens revoques, et le clic
 * suivant redemande.
 *
 * Rien n'est garde ailleurs que dans la page, comme pour le rendu : la reponse et ses liens
 * (`Blob`) vivent avec elle.
 */

/** Ce que la page attend d'un assemblage : ce qu'il faut refaire, et quand la session est perdue. */
type Rappels = {
  /** Le 3MF d'une figurine est illisible : la page relance d'elle-meme les rendus. */
  relancer: () => void;
  surRefus: (erreur: ApiError) => void;
};

type Tenue = { requete: SerreLivresRequete; reponse: SerreLivresReponse };
type EchecTenu = { requete: SerreLivresRequete; echec: Echec; imprimante: Imprimante };

/**
 * Un echec parle-t-il encore de ce qui est a l'ecran ? Il est attache a la requete qui l'a connu,
 * **par identite** : la page en refait une des qu'un rendu, un nom, un miroir ou une teinte change,
 * et un rendu relance qui rend les memes octets en fait une neuve aussi. La reponse gardee, elle, se
 * juge au contenu : les memes 3MF donnent le meme jeu. Pendant que les rendus tournent, il n'y a
 * pas de requete (`null`) : seul le 3MF illisible reste, parce que c'est lui qui les a relances et
 * que sa carte dit « relance… » **tant qu'un rendu tourne**. Des que plus aucun ne tourne (l'un
 * d'eux a echoue, un nom a ete efface), il n'a plus rien a attendre : il s'efface, et la carte ne
 * tourne pas dans le vide.
 */
function echecDeLEcran(
  echec: EchecTenu | null,
  requete: SerreLivresRequete | null,
  rendusEnCours: boolean,
): boolean {
  if (echec === null) return false;
  if (requete === null) return echec.echec.genre === "illisible" && rendusEnCours;
  return echec.requete === requete;
}

export type Assemblage = {
  /** L'imprimante dont l'assemblage est en cours de calcul. */
  envoi: Imprimante | null;
  /** L'echec de la derniere demande, tant qu'il parle de ce qui est a l'ecran. */
  echec: EchecTenu | null;
  /** La reponse gardee, tant qu'elle repond a ce qui est a l'ecran. */
  reponse: SerreLivresReponse | null;
  /** Telecharge le jeu : de la reponse gardee, sinon apres l'avoir demandee. */
  demander: (imprimante: Imprimante) => void;
  /** Redemande ce que l'echec n'a pas obtenu. */
  reessayer: () => void;
  /** Le jeu demande qui s'est trouve refuse : son refus a pris la place de son bouton. */
  refusDemande: Imprimante | null;
  /** Une reponse est revenue apres un changement : elle a ete laissee de cote, il faut redemander. */
  caduc: boolean;
};

export function useAssemblage(
  requete: SerreLivresRequete | null,
  rappels: Rappels,
  rendusEnCours: boolean,
): Assemblage {
  const [tenue, setTenue] = useState<Tenue | null>(null);
  const [envoi, setEnvoi] = useState<Imprimante | null>(null);
  const [echecTenu, setEchecTenu] = useState<EchecTenu | null>(null);
  const [aTelecharger, setATelecharger] = useState<Imprimante | null>(null);
  // L'imprimante dont le jeu, une fois demande, s'est trouve refuse : son bouton est remplace par le
  // refus, et la carte y met le focus.
  const [refusDemande, setRefusDemande] = useState<Imprimante | null>(null);
  // Une reponse (ou un echec) est revenue apres un changement : elle ne dit plus ce qui est a l'ecran.
  const [caduc, setCaduc] = useState(false);
  // Ce que l'ecran montre au moment ou une reponse arrive, relu par la promesse sans en changer
  // l'identite (la meme technique que `useFichiers` des cadres).
  const courante = useRef(requete);
  courante.current = requete;
  const rappelsCourants = useRef(rappels);
  rappelsCourants.current = rappels;
  const enVol = useRef<AbortController | null>(null);

  const reponse = requete && tenue && memeRequete(tenue.requete, requete) ? tenue.reponse : null;
  const echec = echecDeLEcran(echecTenu, requete, rendusEnCours) ? echecTenu : null;
  // Les liens ne vivent qu'avec une reponse qui vaut encore : revoques des qu'elle est ecartee.
  const liens = useLiensDeFichiers(reponse);

  useEffect(
    () => () => {
      enVol.current?.abort();
      enVol.current = null;
    },
    [],
  );

  // Un echec ne parle que de ce qui etait a l'ecran quand il est arrive : des que cela change, il
  // s'efface, et ne revient pas si l'ecran retrouve le meme contenu.
  useEffect(() => {
    if (echecTenu !== null && !echecDeLEcran(echecTenu, requete, rendusEnCours)) setEchecTenu(null);
  }, [requete, echecTenu, rendusEnCours]);

  // Le fichier demande part des que son lien existe : au clic quand la reponse etait deja la, a
  // son arrivee sinon. Une demande qui ne peut plus aboutir (la reponse a ete ecartee) s'efface.
  useEffect(() => {
    if (aTelecharger === null) return;
    if (reponse === null) {
      if (envoi === null) setATelecharger(null);
      return;
    }
    const fichier = fichierDuJeu(reponse, aTelecharger);
    const lien = fichier ? liens[fichier.nom] : undefined;
    if (!fichier) {
      setATelecharger(null);
      setRefusDemande(aTelecharger);
      return;
    }
    if (!lien) return;
    declencherTelechargement(lien, fichier.nom);
    setATelecharger(null);
  }, [aTelecharger, reponse, liens, envoi]);

  const envoyer = (imprimante: Imprimante, aEnvoyer: SerreLivresRequete): void => {
    setEchecTenu(null);
    if (envoiTropLourd(aEnvoyer)) {
      setEchecTenu({ requete: aEnvoyer, echec: { genre: "trop-lourd" }, imprimante });
      return;
    }
    const controleur = new AbortController();
    enVol.current = controleur;
    setEnvoi(imprimante);
    envoyerAssemblage(aEnvoyer, controleur.signal).then(
      (recue) => {
        if (controleur.signal.aborted) return;
        enVol.current = null;
        setEnvoi(null);
        // Ce qui est a l'ecran a change pendant le calcul : la reponse ne le dit plus.
        if (courante.current === null || !memeRequete(courante.current, aEnvoyer)) {
          setCaduc(true);
          return;
        }
        setTenue({ requete: courante.current, reponse: recue });
        setATelecharger(imprimante);
      },
      (erreur: unknown) => {
        if (controleur.signal.aborted) return;
        enVol.current = null;
        setEnvoi(null);
        if (erreur instanceof ApiError && (erreur.status === 401 || erreur.status === 403)) {
          rappelsCourants.current.surRefus(erreur);
          return;
        }
        if (courante.current === null || !memeRequete(courante.current, aEnvoyer)) {
          setCaduc(true);
          return;
        }
        const classe = classerEchec(erreur);
        setEchecTenu({ requete: courante.current, echec: classe, imprimante });
        if (classe.genre === "illisible") rappelsCourants.current.relancer();
      },
    );
  };

  const demander = (imprimante: Imprimante): void => {
    if (requete === null || envoi !== null) return;
    setRefusDemande(null);
    setCaduc(false);
    if (reponse !== null) {
      setEchecTenu(null);
      setATelecharger(imprimante);
      return;
    }
    envoyer(imprimante, requete);
  };

  return {
    envoi,
    echec,
    reponse,
    demander,
    reessayer: () => {
      if (echec) demander(echec.imprimante);
    },
    refusDemande,
    caduc,
  };
}

/** La roue d'un calcul en cours, comme celle de la carte Apercu. */
export function Roue({ className }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "inline-block size-4 shrink-0 animate-spin rounded-full border-2 border-border border-t-foreground motion-reduce:animate-none",
        className,
      )}
    />
  );
}

/** Un echec de l'assemblage, en tete de la carte. Reessayer et « relance… » suivent le tableau de la spec. */
function EchecDeLAssemblage({
  echec,
  noms,
  reessayer,
}: {
  echec: Echec;
  noms: Record<Colonne, string>;
  reessayer: () => void;
}) {
  const texte = texteDEchec(echec, noms);
  const avecAction = texte.reessayer || texte.relance;
  return (
    <div
      className={cn(
        texte.ton === "rouge" ? ALERTE.rouge : ALERTE.grise,
        "mt-3.5",
        avecAction && "flex items-center justify-between gap-3",
      )}
      role="alert"
    >
      <span className="min-w-0 flex-1">
        <strong className="font-semibold">{texte.titre}</strong>
        {texte.suite}
      </span>
      {texte.reessayer ? (
        <Button
          className="min-h-11 shrink-0 md:min-h-8"
          onClick={reessayer}
          size="sm"
          variant="outline"
        >
          Réessayer
        </Button>
      ) : null}
      {texte.relance ? (
        <span className="inline-flex shrink-0 items-center gap-2 text-[13px] text-muted-foreground">
          <Roue />
          relance…
        </span>
      ) : null}
    </div>
  );
}

// Eteint par `aria-disabled` et non par `disabled` : un bouton `disabled` perd le focus, et celui qu'on
// vient de cliquer le perdrait pendant l'assemblage.
const BOUTON_JEU =
  "inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-md border px-3.5 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card aria-disabled:cursor-default md:min-h-10";

/** Un jeu : son bouton, ses plateaux et son imprimante, ou la raison pour laquelle l'imprimante le refuse. */
function JeuComplet({
  imprimante,
  allume,
  envoi,
  demander,
  reponse,
  plateauxAvant,
  raisonId,
  refusRef,
}: {
  imprimante: Imprimante;
  /** Les telechargements sont allumes : tout est la. */
  allume: boolean;
  envoi: Imprimante | null;
  demander: (imprimante: Imprimante) => void;
  reponse: SerreLivresReponse | null;
  /** Les plateaux avant la reponse, par la regle de `products`. */
  plateauxAvant: string[] | null;
  /** Le paragraphe qui dit pourquoi les boutons sont eteints : leur description tant qu'ils le sont. */
  raisonId: string;
  /** Le refus de ce jeu, quand il y en a un : la carte y met le focus. */
  refusRef: (element: HTMLElement | null) => void;
}) {
  const jeu = reponse ? jeuDe(reponse, imprimante) : undefined;
  if (jeu && jeu.refus !== null) {
    return (
      <div>
        <p
          className={cn(
            ALERTE.rouge,
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card",
          )}
          data-refus={imprimante}
          ref={refusRef}
          tabIndex={-1}
        >
          <strong className="font-semibold">{phraseDuRefus(jeu.refus)}</strong> {SUITE_DU_REFUS}
        </p>
      </div>
    );
  }
  const plateaux = allume ? (jeu?.plateaux ?? plateauxAvant) : null;
  const principal = imprimante === "p1s";
  const eteint = !allume || envoi !== null;
  return (
    <div>
      <button
        aria-describedby={allume ? undefined : raisonId}
        aria-disabled={eteint || undefined}
        className={cn(
          BOUTON_JEU,
          principal
            ? "border-primary bg-primary text-primary-foreground hover:opacity-90 aria-disabled:border-transparent aria-disabled:bg-muted-foreground/30 aria-disabled:text-muted-foreground aria-disabled:hover:opacity-100"
            : "border-input bg-card text-foreground hover:bg-muted aria-disabled:border-border aria-disabled:bg-muted/50 aria-disabled:text-muted-foreground aria-disabled:hover:bg-muted/50",
        )}
        onClick={() => {
          if (!eteint) demander(imprimante);
        }}
        type="button"
      >
        {envoi === imprimante ? (
          <>
            <Roue />
            {LIBELLE_ASSEMBLAGE_EN_COURS}
          </>
        ) : (
          libelleDuTelechargement(imprimante)
        )}
      </button>
      {plateaux ? (
        <ul className="mt-2 list-disc pl-[18px] text-[13.5px]">
          {plateaux.map((nom, rang) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: deux plateaux portent le meme nom (« Bookend », « Bookend »), le rang les distingue
            <li key={rang}>
              Plateau {rang + 1} : {nom}
            </li>
          ))}
        </ul>
      ) : null}
      {allume ? (
        <p className="mt-1.5 text-[13px] text-muted-foreground">
          {notePourLImprimante(imprimante)}
        </p>
      ) : null}
    </div>
  );
}

/**
 * La carte Fichiers du mode serre-livres : les deux boutons, leurs plateaux, ce qui les eteint, ce
 * qui a echoue. `requete` est ce que l'ecran dit d'un assemblage : `null` tant qu'il manque une
 * image, un nom, un rendu a jour ou le catalogue des teintes, et `raison` dit lequel.
 */
export function CarteFichiers({
  requete,
  raison,
  noms,
  filaments,
  relancerLesRendus,
  rendusEnCours,
  surRefus,
}: {
  requete: SerreLivresRequete | null;
  raison: string | null;
  /** Les noms tels qu'on les montre : celui qui est tape, sinon « la figurine de gauche ». */
  noms: Record<Colonne, string>;
  /** Les filaments de chaque figurine, pour nommer ses plateaux avant la reponse. */
  filaments: Record<Colonne, number>;
  relancerLesRendus: () => void;
  /** Un des deux rendus tourne : le « relance… » d'un 3MF illisible a encore quelque chose a attendre. */
  rendusEnCours: boolean;
  surRefus: (erreur: ApiError) => void;
}) {
  const id = useId();
  const titre = useRef<HTMLHeadingElement>(null);
  const assemblage = useAssemblage(
    requete,
    { relancer: relancerLesRendus, surRefus },
    rendusEnCours,
  );
  // Un jeu refuse remplace son bouton : si c'est celui qu'on vient de demander, le focus va a son refus.
  const refus = useRef<Record<Imprimante, HTMLElement | null>>({ p1s: null, a1mini: null });
  // Une reponse est revenue apres un changement : elle est laissee de cote, et les boutons sont de nouveau
  // allumes (sinon la phrase de la carte dit deja ce qui manque).
  const caduc = assemblage.caduc && requete !== null;
  const { refusDemande } = assemblage;
  useEffect(() => {
    if (refusDemande !== null) refus.current[refusDemande]?.focus();
  }, [refusDemande]);
  // Reessayer disparait avec l'echec qui le portait : le focus va au titre de la carte.
  const reessayer = (): void => {
    assemblage.reessayer();
    titre.current?.focus();
  };

  return (
    <section aria-labelledby={`${id}-titre`} className={CARTE}>
      <h2
        className="rounded-sm text-[15px] font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card"
        id={`${id}-titre`}
        ref={titre}
        tabIndex={-1}
      >
        Fichiers
      </h2>
      <p className="mt-1 text-sm text-muted-foreground" id={`${id}-raison`}>
        {raison ?? TITRE_DES_FICHIERS_PRETS}
      </p>

      {/*
       * Toujours montee, pour que le lecteur d'ecran annonce le texte quand il y arrive : une zone
       * `role="status"` qui apparait deja pleine ne l'est pas. Videe, elle ne prend pas de place
       * (le meme modele que la zone de calcul d'une colonne).
       */}
      <p className={cn(caduc ? ALERTE.grise : "sr-only", caduc && "mt-3.5")} role="status">
        {caduc ? TEXTE_ASSEMBLAGE_CADUC : null}
      </p>

      {assemblage.echec ? (
        <EchecDeLAssemblage echec={assemblage.echec.echec} noms={noms} reessayer={reessayer} />
      ) : null}

      <div className="mt-3.5 grid gap-4 lg:grid-cols-2">
        {IMPRIMANTES.map((imprimante) => (
          <JeuComplet
            allume={requete !== null}
            demander={assemblage.demander}
            envoi={assemblage.envoi}
            imprimante={imprimante}
            key={imprimante}
            plateauxAvant={
              requete
                ? plateauxAvantAssemblage(
                    imprimante,
                    { nom: requete.gauche.nom, filaments: filaments.gauche },
                    { nom: requete.droite.nom, filaments: filaments.droite },
                  )
                : null
            }
            raisonId={`${id}-raison`}
            refusRef={(element) => {
              refus.current[imprimante] = element;
            }}
            reponse={assemblage.reponse}
          />
        ))}
      </div>
    </section>
  );
}
